"use client";

import { ArrowRight, Copy, Check } from "lucide-react";
import { Button } from "./Button";
import { AnimateOnScroll } from "./index";
import { useState } from "react";

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

const INSTALL_COMMAND = "curl -fsSL https://specledger.io/install.sh | bash";

export function CTA() {
  const [copied, setCopied] = useState(false);

  const openCalendly = () => {
    const calendlyUrl = process.env.NEXT_PUBLIC_CALENDLY_URL || "https://calendly.com/sean89/30-minute-meeting";

    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: calendlyUrl });
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.onload = () => {
      window.Calendly?.initPopupWidget({ url: calendlyUrl });
    };
    document.head.appendChild(script);
  };

  const copyCommand = async () => {
    await navigator.clipboard.writeText(INSTALL_COMMAND);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary to-accent text-center relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/5 w-1/2 h-full bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,transparent_50%)]" />
        <div className="absolute top-0 right-1/5 w-1/2 h-full bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,transparent_50%)]" />
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-[700px] mx-auto">
          <AnimateOnScroll variant="fade-up">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[44px] font-extrabold text-white mb-3 sm:mb-4 tracking-tight">
              Ready to build aligned?
            </h2>
          </AnimateOnScroll>
          <AnimateOnScroll variant="fade-up" delay={100}>
            <p className="text-sm sm:text-base md:text-lg text-white/80 mb-6 sm:mb-8 md:mb-10">
              Join teams using Specledger for spec-driven AI development.
            </p>
          </AnimateOnScroll>

          <AnimateOnScroll variant="scale" delay={200}>
            <div className="bg-white/15 backdrop-blur-lg border border-white/20 rounded-lg sm:rounded-xl md:rounded-[14px] px-3 py-2.5 sm:px-4 sm:py-3 md:px-5 md:py-3.5 flex items-center gap-3 font-mono text-xs sm:text-sm text-white mb-6 sm:mb-8 max-w-full sm:max-w-fit mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin">
                <span className="text-white/60 flex-shrink-0">$</span>
                <span className="whitespace-nowrap">{INSTALL_COMMAND}</span>
              </div>
              <button
                onClick={copyCommand}
                className="flex-shrink-0 p-1.5 hover:bg-white/10 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                aria-label={copied ? "Copied to clipboard" : "Copy install command to clipboard"}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-400" aria-hidden="true" />
                ) : (
                  <Copy className="w-4 h-4 text-white/60 hover:text-white" aria-hidden="true" />
                )}
              </button>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll variant="fade-up" delay={300}>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="white"
                size="lg"
                href="https://app.specledger.io/login"
                className="w-full sm:w-auto"
              >
                Get Started Free <ArrowRight className="w-4 h-4 ml-1 inline" aria-hidden="true" />
              </Button>
              <Button
                variant="outline-white"
                size="lg"
                onClick={openCalendly}
                className="w-full sm:w-auto"
              >
                Book a Demo
              </Button>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
}
